import { useChat } from "@ai-sdk/react";
import type { UIMessage } from "ai";
import { useEffect } from "react";
import {
  useAutomationRunStreamChatId,
  useAutomationRunStreamTransport,
  useIsMountSettled,
} from "./automationRunStreamProvider.js";

export interface AutomationRunMessagesLive {
  /** The agent's conversation so far, updated as the thread streams. */
  messages: UIMessage[];
}

/**
 * Batches streamed message updates so a reconnect's backlog replay re-renders
 * the transcript a few times per second instead of once per chunk.
 */
const MESSAGE_RENDER_THROTTLE_MS = 150;

/**
 * Tails one in-flight agent thread — the run's orchestrator (threadId ===
 * runId) or a subagent — as an AI SDK v6 UI message stream. `useChat`'s
 * `resume` flow reconnects (read-only — no message is sent) through the
 * ambient `AutomationRunStreamProvider` transport, which demultiplexes the
 * run's single live connection by thread id. A finished run has no live
 * stream, so the reconnect resolves to null and `messages` stays empty —
 * callers fall back to the persisted transcript once terminal.
 *
 * The returned `messages` carry the same `UIMessage` shape the persisted
 * transcript uses, so they render through the unchanged `RunTranscript`.
 */
export function useAutomationRunMessagesLive(options: {
  threadId: string;
}): AutomationRunMessagesLive {
  const { threadId } = options;

  // The chatId (the generation-scoped thread id) selects this thread on the
  // multiplexed stream; a generation bump restarts the chat from the replay.
  const transport = useAutomationRunStreamTransport();
  const chatId = useAutomationRunStreamChatId(threadId);

  // Deferring resume past the mount keeps StrictMode's discarded dev mount
  // from consuming a demultiplexed stream alongside the surviving mount.
  const isMountSettled = useIsMountSettled();

  // We drive `resume` ourselves rather than via `useChat`'s `resume` option:
  // that option only resumes on mount (its effect ignores id changes), but a
  // generation bump (reconnect replay, `thread_reset`) swaps `chatId`, which
  // recreates the underlying chat with an empty message list. Re-resuming on
  // each `chatId` reconnects that fresh chat to the replayed stream — without
  // it the new generation never subscribes and the transcript stays empty.
  const { messages, resumeStream } = useChat<UIMessage>({
    id: chatId,
    transport,
    experimental_throttle: MESSAGE_RENDER_THROTTLE_MS,
  });

  useEffect(() => {
    if (isMountSettled) {
      void resumeStream();
    }
  }, [isMountSettled, chatId, resumeStream]);

  return { messages };
}
